import { useSyncExternalStore, type ReactElement } from "react";

// Preferencia visual por navegador. No viaja al backend ni afecta permisos o datos.
export type Theme = "papel" | "clasico" | "cristal";
const STORAGE_KEY = "abora-portal-theme";
const THEMES: { id: Theme; label: string; hint: string }[] = [
  { id: "papel", label: "Papel", hint: "claro" },
  { id: "clasico", label: "Clásico", hint: "intermedio" },
  { id: "cristal", label: "Cristal", hint: "oscuro" },
];
const listeners = new Set<() => void>();

function readStoredTheme(): Theme {
  try {
    const value = window.localStorage.getItem(STORAGE_KEY);
    if (value === "papel" || value === "clasico" || value === "cristal") return value;
  } catch {
    // Almacenamiento no disponible (modo privado, política del navegador): se usa el tema por defecto.
  }
  return "clasico";
}

let current: Theme = typeof window === "undefined" ? "clasico" : readStoredTheme();

export function applyStoredTheme() {
  document.documentElement.dataset.theme = current;
}

function setTheme(theme: Theme) {
  current = theme;
  document.documentElement.dataset.theme = theme;
  try {
    window.localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    // Sin persistencia: el cambio se mantiene en esta pestaña.
  }
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

const ICONS: Record<Theme, ReactElement> = {
  papel: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true" focusable="false"><circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M2 12h2M20 12h2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" /></svg>,
  clasico: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true" focusable="false"><circle cx="12" cy="12" r="9" /><path d="M12 3a9 9 0 0 1 0 18Z" fill="currentColor" /></svg>,
  cristal: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" aria-hidden="true" focusable="false"><path d="M20 14.5A8 8 0 1 1 9.5 4a6.5 6.5 0 0 0 10.5 10.5Z" /></svg>,
};

export function ThemeSwitcher({ className = "" }: { className?: string }) {
  const theme = useSyncExternalStore(subscribe, () => current, () => current);
  return (
    <div className={`theme-switcher ${className}`.trim()} role="group" aria-label="Tema de la interfaz">
      {THEMES.map((item) => (
        <button
          key={item.id}
          type="button"
          aria-pressed={theme === item.id}
          title={`Tema ${item.label} (${item.hint})`}
          onClick={() => setTheme(item.id)}
        >
          {ICONS[item.id]}
          <span>{item.label}</span>
        </button>
      ))}
    </div>
  );
}
